import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { ChevronRight, Check } from 'lucide-react';
import { BusinessGoalSkeleton } from '@/components/company/skeletons';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import { useCompanyProfile, useUpdateCompanyField } from '@/hooks/useCompanyProfile';
import VideoPlayer from '@/components/VideoPlayer';
import KinerjaTable from './KinerjaTable';
import ProyeksiTable from './ProyeksiTable';
import ProyeksiInputTable from './ProyeksiInputTable';
import {
  TargetType,
  MonthlyRevenueData,
  BusinessGoalKinerjaData,
  BusinessGoalStartupData,
  ProjectionMonthlyTargetData,
  getActualYears,
  getProjectionYears,
  calcAverageKinerja,
  createEmptyMonthlyData,
  createEmptyProjectionMonthlyData,
  isKinerjaData,
  isStartupData,
} from './kinerja.types';

type BusinessType = 'baru' | 'berjalan' | null;

const CURRENT_YEAR = new Date().getFullYear();

const BUSINESS_TYPE_LABELS: Record<Exclude<BusinessType, null>, string> = {
  baru: 'Usaha Baru',
  berjalan: 'Usaha Telah Berjalan',
};

interface BusinessGoalKinerjaProps {
  videoLink?: string;
  tips?: string;
}

function updateProjectionValues(
  currentValues: number[],
  projectionYears: number[],
  year: number,
  value: number,
): number[] {
  const targetIndex = projectionYears.indexOf(year);
  if (targetIndex < 0) return currentValues;
  const nextValues = [...currentValues];
  nextValues[targetIndex] = value;
  return nextValues;
}

type ResolvedBusinessGoal =
  | { businessType: 'berjalan'; data: BusinessGoalKinerjaData }
  | { businessType: 'baru'; data: BusinessGoalStartupData }
  | { businessType: null };

function parseKinerjaData(raw: Record<string, unknown>): BusinessGoalKinerjaData {
  return {
    target_type: (raw.target_type as 'rupiah' | 'volume') ?? 'rupiah',
    monthly_data: Array.isArray(raw.monthly_data) ? (raw.monthly_data as BusinessGoalKinerjaData['monthly_data']) : [],
    projection_kinerja_pct: typeof raw.projection_kinerja_pct === 'number' ? raw.projection_kinerja_pct : 0,
    business_type: 'berjalan',
  };
}

function parseStartupData(raw: Record<string, unknown>): BusinessGoalStartupData {
  return {
    target_type: (raw.target_type as 'rupiah' | 'volume') ?? 'rupiah',
    projection_years: Array.isArray(raw.projection_years) ? (raw.projection_years as number[]) : [],
    projection_monthly_data: Array.isArray(raw.projection_monthly_data)
      ? (raw.projection_monthly_data as BusinessGoalStartupData['projection_monthly_data'])
      : [],
    business_type: 'baru',
  };
}

/** Parses the raw `business_goal` field (possibly wrapped in a `value` envelope) into a typed shape. */
function resolveBusinessGoal(businessGoalData: unknown): ResolvedBusinessGoal {
  // Resolve the raw value (may be nested under a `value` envelope)
  let rawValue: unknown = businessGoalData;
  if (
    typeof businessGoalData === 'object' &&
    businessGoalData !== null &&
    'value' in (businessGoalData as Record<string, unknown>) &&
    !isKinerjaData(businessGoalData) &&
    !isStartupData(businessGoalData)
  ) {
    rawValue = (businessGoalData as { value?: unknown }).value;
  }

  // Prefer explicit business_type field saved by this version of the FE.
  // Explicit type wins — do NOT consult shape detection when present.
  const explicitType =
    typeof rawValue === 'object' && rawValue !== null ? (rawValue as Record<string, unknown>).business_type : undefined;

  if (explicitType === 'berjalan') {
    return { businessType: 'berjalan', data: parseKinerjaData(rawValue as Record<string, unknown>) };
  }

  if (explicitType === 'baru') {
    return { businessType: 'baru', data: parseStartupData(rawValue as Record<string, unknown>) };
  }

  if (isKinerjaData(rawValue)) {
    return { businessType: 'berjalan', data: rawValue };
  }

  if (isStartupData(rawValue)) {
    return { businessType: 'baru', data: rawValue };
  }

  return { businessType: null };
}

export default function BusinessGoalKinerja({ videoLink, tips }: Readonly<BusinessGoalKinerjaProps>) {
  const { currentTenant } = useTenantState();
  const isReadOnly = useIsReadOnly();
  const { companyProfile } = useCompanyState();
  const { isLoading } = useCompanyProfile(currentTenant?.id);
  const updateFieldMutation = useUpdateCompanyField();
  const navigate = useNavigate();
  const { tenant_id } = useParams();

  const [showPanel, setShowPanel] = useState(true);
  const [businessType, setBusinessType] = useState<BusinessType>(null);
  const [targetType, setTargetType] = useState<TargetType>('rupiah');
  const [monthlyData, setMonthlyData] = useState<MonthlyRevenueData[]>(createEmptyMonthlyData());
  const [projectionKinerjaPct, setProjectionKinerjaPct] = useState<number>(0);
  const [isKinerjaPctManuallyEdited, setIsKinerjaPctManuallyEdited] = useState(false);
  const [projectionYears, setProjectionYears] = useState<number[]>(getProjectionYears(CURRENT_YEAR));
  const [projectionMonthlyData, setProjectionMonthlyData] = useState<ProjectionMonthlyTargetData[]>(
    createEmptyProjectionMonthlyData(getProjectionYears(CURRENT_YEAR)),
  );
  const [isPublished, setIsPublished] = useState(false);
  const [year1, year2] = getActualYears(CURRENT_YEAR);
  const [triggerEditMode, setTriggerEditMode] = useState(0);

  useEffect(() => {
    if (!companyProfile) return;

    const resolved = resolveBusinessGoal(companyProfile.business_goal);

    if (resolved.businessType === 'berjalan') {
      const { data } = resolved;
      setBusinessType('berjalan');
      setTargetType(data.target_type ?? 'rupiah');
      const empty = createEmptyMonthlyData();
      const merged = empty.map(emptyRow => data.monthly_data.find(d => d.month === emptyRow.month) ?? emptyRow);
      setMonthlyData(merged);
      const savedPct = data.projection_kinerja_pct ?? 0;
      setProjectionKinerjaPct(savedPct);
      setIsKinerjaPctManuallyEdited(savedPct > 0);
    } else if (resolved.businessType === 'baru') {
      const { data } = resolved;
      setBusinessType('baru');
      const years = data.projection_years?.length ? data.projection_years : getProjectionYears(CURRENT_YEAR);
      const emptyStartup = createEmptyProjectionMonthlyData(years);
      const mergedStartup = emptyStartup.map(emptyRow => {
        const existing = data.projection_monthly_data.find(d => d.month === emptyRow.month);
        return {
          month: emptyRow.month,
          values: years.map((_, idx) => existing?.values[idx] ?? 0),
        };
      });
      setTargetType(data.target_type ?? 'rupiah');
      setProjectionYears(years);
      setProjectionMonthlyData(mergedStartup);
    } else {
      setBusinessType(null);
    }

    setIsPublished(!!(companyProfile as { is_published_business_goal?: boolean })?.is_published_business_goal);
  }, [companyProfile]);

  const averageKinerja = calcAverageKinerja(monthlyData);

  const handleMonthlyDataChange = useCallback(
    (month: number, field: 'revenue_year1' | 'revenue_year2', value: number) => {
      setMonthlyData(prev => prev.map(d => (d.month === month ? { ...d, [field]: value } : d)));
      setIsKinerjaPctManuallyEdited(false);
    },
    [],
  );

  const handleProjectionKinerjaPctChange = useCallback((pct: number) => {
    setProjectionKinerjaPct(pct);
    setIsKinerjaPctManuallyEdited(true);
  }, []);

  const handleProjectionMonthlyChange = useCallback(
    (month: number, year: number, value: number) => {
      setProjectionMonthlyData(prev =>
        prev.map(row => {
          if (row.month !== month) return row;
          const values = updateProjectionValues(row.values, projectionYears, year, value);
          return { ...row, values };
        }),
      );
    },
    [projectionYears],
  );

  const buildSavePayload = (): BusinessGoalKinerjaData | BusinessGoalStartupData => {
    if (businessType === 'baru') {
      return {
        target_type: targetType,
        projection_years: projectionYears,
        projection_monthly_data: projectionMonthlyData,
        business_type: 'baru',
      };
    }
    return {
      target_type: targetType,
      monthly_data: monthlyData,
      projection_kinerja_pct: projectionKinerjaPct,
      business_type: 'berjalan',
    };
  };

  const handleSaveAll = async () => {
    if (!currentTenant?.id) return;

    if (businessType === 'berjalan' && (projectionKinerjaPct < 0 || projectionKinerjaPct > 100)) {
      const result = await Swal.fire({
        title: 'Peringatan',
        text: 'Kenaikan target berada di luar batas wajar (0% - 100%). Tetap gunakan nilai ini atau modifikasi kembali?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Tetap Gunakan',
        cancelButtonText: 'Modifikasi Target',
      });

      if (!result.isConfirmed) {
        setTriggerEditMode(Date.now());
        setTimeout(() => {
          const el = document.getElementById('proyeksi-table-container');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => {
              document.getElementById('edit-pct-input')?.focus();
            }, 300);
          }
        }, 100);
        return;
      }
    }

    try {
      await updateFieldMutation.mutateAsync({
        tenantId: currentTenant.id,
        field: 'business_goal',
        value: buildSavePayload(),
        isPublished,
        showSuccessToast: false,
      });
      toast.success('business goal berhasil diperbarui');
      navigate(`/${tenant_id}/organization-structure`);
    } catch {
      toast.error('Gagal menyimpan data kinerja perusahaan');
    }
  };

  const handleBusinessTypeChange = (newType: BusinessType) => {
    if (newType === businessType) return;
    setMonthlyData(createEmptyMonthlyData());
    setProjectionKinerjaPct(0);
    setIsKinerjaPctManuallyEdited(false);
    const defaultYears = getProjectionYears(CURRENT_YEAR);
    setProjectionYears(defaultYears);
    setProjectionMonthlyData(createEmptyProjectionMonthlyData(defaultYears));
    setBusinessType(newType);
  };

  useEffect(() => {
    if (businessType === 'berjalan' && averageKinerja !== null && !isKinerjaPctManuallyEdited) {
      setProjectionKinerjaPct(Number.parseFloat(averageKinerja.toFixed(2)));
    }
  }, [averageKinerja, businessType, isKinerjaPctManuallyEdited]);

  if (isLoading) {
    return <BusinessGoalSkeleton />;
  }

  const isSaving = updateFieldMutation.isLoading;

  return (
    <section className="w-full">
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg">Business Goals</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            Merancang target pertumbuhan bisnis selama 5 tahun ke depan, untuk mendukung tercapainya Vision.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          {!isReadOnly && businessType !== null && (
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={isSaving}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
              style={{ background: '#FD6118' }}
            >
              <Check size={15} />
              {isSaving ? 'Menyimpan...' : 'Simpan & Lanjut'}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 px-5 py-4">
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-6">
            <div className="mb-6">
              <label htmlFor="business-type" className="text-sm font-medium text-gray-700 dark:text-white block mb-2">
                Tipe Usaha
              </label>
              {isReadOnly ? (
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {businessType ? BUSINESS_TYPE_LABELS[businessType] : '-'}
                </span>
              ) : (
                <select
                  id="business-type"
                  value={businessType ?? ''}
                  onChange={e => {
                    const val = e.target.value;
                    handleBusinessTypeChange(val === 'baru' || val === 'berjalan' ? val : null);
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white min-w-[200px] max-w-full sm:max-w-xs"
                >
                  <option value="">-- Pilih Tipe Usaha --</option>
                  <option value="baru">Usaha Baru</option>
                  <option value="berjalan">Usaha Telah Berjalan</option>
                </select>
              )}
            </div>

            {businessType !== null && (
              <>
                {!isReadOnly && (
                  <div className="mb-6">
                    <label
                      htmlFor="target-type"
                      className="text-sm font-medium text-gray-700 dark:text-white block mb-2"
                    >
                      Tentukan target Anda dalam
                    </label>
                    <select
                      id="target-type"
                      value={targetType}
                      onChange={e => setTargetType(e.target.value as TargetType)}
                      className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white min-w-[200px] max-w-full sm:max-w-xs"
                    >
                      <option value="rupiah">Rupiah</option>
                      <option value="volume">Volume</option>
                    </select>
                  </div>
                )}

                {isReadOnly && (
                  <div className="mb-4">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Target dalam: </span>
                    <span className="text-sm font-medium text-gray-700 dark:text-white capitalize">{targetType}</span>
                  </div>
                )}

                {businessType === 'baru' ? (
                  <ProyeksiInputTable
                    targetType={targetType}
                    projectionYears={projectionYears}
                    projectionMonthlyData={projectionMonthlyData}
                    isReadOnly={isReadOnly}
                    onChange={handleProjectionMonthlyChange}
                  />
                ) : (
                  <>
                    <h2 className="text-base font-semibold text-gray-800 dark:text-white text-center mb-2">
                      Realisasi Actual Kinerja Perusahaan
                    </h2>
                    <KinerjaTable
                      targetType={targetType}
                      year1={year1}
                      year2={year2}
                      monthlyData={monthlyData}
                      isReadOnly={isReadOnly}
                      onChange={handleMonthlyDataChange}
                    />
                    <ProyeksiTable
                      targetType={targetType}
                      monthlyData={monthlyData}
                      projectionYears={projectionYears}
                      projectionKinerjaPct={projectionKinerjaPct}
                      isReadOnly={isReadOnly}
                      isKinerjaPctManuallyEdited={isKinerjaPctManuallyEdited}
                      onProjectionKinerjaPctChange={handleProjectionKinerjaPctChange}
                      triggerEditMode={triggerEditMode}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {showPanel && (videoLink || tips) && (
          <div className="w-full xl:w-80 shrink-0">
            <div className="bg-blue-50 dark:bg-gray-800 border border-blue-100 dark:border-gray-700 rounded-xl p-5 space-y-4 xl:sticky xl:top-4 xl:max-h-[calc(100vh-8rem)] xl:overflow-y-auto">
              <div className="flex items-center gap-2">
                <span className="text-xl">💡</span>
                <h3 className="font-bold text-base text-[#1848FD] dark:text-white">Tips Pengisian</h3>
              </div>

              {tips && (
                <div
                  className="text-sm text-gray-600 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: tips }}
                />
              )}

              {videoLink && (
                <div className="pt-3 border-t border-blue-200 dark:border-gray-600">
                  <VideoPlayer url={videoLink} tipsHtml={tips} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
